/**
 * Session Provider
 * Manages authentication session state with React Query
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { env } from "@/lib/env";
import { refreshAccessToken, setTokenRefreshCallback } from "@/lib/apiClient";
import type {
  UserProfile,
  SeatPreview,
  ContainerAccess,
  PendingInvite,
} from "@/types/session";
import {
  mapContainerFromList,
  mapContainerFromSelect,
} from "../lib/container-access";
import {
  getAuthItem,
  setAuthItem,
  removeAuthItem,
  clearAuthStorage,
} from "@/lib/authStorage";
import { readJwtExpSeconds } from "../lib/jwt";
import { clearSsoAutoConnectAttempted } from "../lib/sso-autoconnect";
import { diag, summarizeContainer } from "@/lib/diag";
import {
  getSubFromToken,
  readWarmupFlag,
  writeWarmupFlag,
  clearWarmupFlag,
} from "../lib/warmup-flag";
import {
  AUTH_BASE_URL,
  fetchWithTimeout,
  refreshAccessTokenWithStatus,
} from "../lib/session-api";
import {
  decodeJwtPayload,
  getActiveWorkspaceIdFromToken,
} from "../lib/jwt-claims";
import {
  TOKEN_STORAGE_KEY,
  GUEST_ID_STORAGE_KEY,
  clearUserSpecificStorage,
  clearEmailCaches,
} from "../lib/session-storage";
import { SELECTED_CONTAINER_KEY } from "@/lib/api/workspace-context";
import type {
  AuthStatus,
  SessionError,
  ProfileUpdate,
  SessionContextValue,
} from "../types";

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

// Per-user warmup flag helpers — extracted into ../lib/warmup-flag.ts so the
// persistence layer is unit-testable without rendering a React tree (see
// warmup-flag.test.ts). The SessionProvider keeps the React-side wiring;
// the helpers know nothing about React.

export function SessionProvider({ children }: { children: ReactNode }) {
  // Load token from storage on mount
  // Native: localStorage (persists across app restarts)
  // Web: sessionStorage (clears when tab closes)
  const [token, setTokenState] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return getAuthItem(TOKEN_STORAGE_KEY);
    }
    return null;
  });

  // Keep apiClient's token refresh in sync with React state so components don't
  // keep sending stale expired tokens after a background 401→refresh cycle.
  useEffect(() => {
    setTokenRefreshCallback(setTokenState);
    return () => setTokenRefreshCallback(() => {});
  }, []);

  // 3-state auth status: checking | authenticated | unauthenticated.
  // Default 'checking' (the critical anti-premature-redirect default). EXCEPTION:
  // returning from an INLINE OAuth connect — the backend only 302s back with
  // integration_connected/calendar_connected for a valid session, and we still
  // hold a non-expired seeded token — so start 'authenticated' and skip the
  // "Restoring session…" screen. A scope grant the user did while already logged
  // in must not look like a re-login. We start 'authenticated' (never
  // 'unauthenticated'), so there is still no premature /login redirect; the
  // post-auth effect re-fetches profile and a genuinely-stale token is caught by
  // its 401 handling.
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (typeof window === "undefined") return "checking";
    try {
      const params = new URLSearchParams(window.location.search);
      const isOAuthReturn =
        params.get("integration_connected") === "true" ||
        params.get("calendar_connected") === "true";
      if (!isOAuthReturn) return "checking";
      const seeded = getAuthItem(TOKEN_STORAGE_KEY);
      const exp = decodeJwtPayload(seeded)?.exp;
      if (seeded && typeof exp === "number" && exp * 1000 > Date.now() + 5000) {
        return "authenticated";
      }
    } catch {
      /* fall through to checking */
    }
    return "checking";
  });

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [seatPreview, setSeatPreview] = useState<SeatPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Container/team state
  const [containers, setContainers] = useState<ContainerAccess[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [selectedContainer, setSelectedContainer] =
    useState<ContainerAccess | null>(() => {
      // Try to restore selected container from storage
      if (typeof window !== "undefined") {
        const stored = getAuthItem(SELECTED_CONTAINER_KEY);
        if (stored) {
          try {
            return JSON.parse(stored);
          } catch {
            return null;
          }
        }
      }
      return null;
    });
  const [sessionError, setSessionError] = useState<SessionError | null>(null);
  // See SessionContextValue.containersLoaded.
  const [containersLoaded, setContainersLoaded] = useState(false);

  // Start warmup complete for returning users (#2568): a stored token + a
  // persisted warmup flag for the same user id means the container is known
  // provisioned, so skip the splash; warmup still refreshes caches in the bg.
  const [warmupComplete, setWarmupComplete] = useState(() => {
    if (typeof window === "undefined") return false;
    const initialToken = getAuthItem(TOKEN_STORAGE_KEY);
    if (!initialToken) return false;
    return readWarmupFlag(getSubFromToken(initialToken));
  });

  const clearSessionError = useCallback(() => {
    setSessionError(null);
  }, []);

  // Backwards compatibility: derive isLoading from status
  const isLoading = status === "checking";

  // Sweep the SSO auto-redirect one-shot flag whenever the session ends
  // (explicit logout via clearSession, OR a stale/invalid token that bootstrap
  // resolves to `unauthenticated` — e.g. after a DB reset). The flag is per-tab,
  // not per-identity, so without this a flag set by one sign-in's connect-first
  // flow would block the next sign-in's SSO auto-redirect in the same tab and
  // strand it on the manual chooser (mirrors the PAYWALL_DISMISSED_KEY sweep).
  useEffect(() => {
    if (status === "unauthenticated") clearSsoAutoConnectAttempted();
  }, [status]);

  // Used to ignore stale async completions (StrictMode / fast remount)
  const bootstrapRunId = useRef(0);
  const containerSyncAttemptRef = useRef<string | null>(null);
  const warmupAttemptedRef = useRef(false);
  // Tracks the user id from the previous token so we can distinguish a real
  // user switch from a token rotation for the same user (refresh). Only the
  // former should reset the warmup state. See #2568.
  const prevWarmupUserIdRef = useRef<string | null>(getSubFromToken(token));
  const prevIsPaidRef = useRef<boolean | undefined>(undefined);

  // Increments on every token-change effect run so stale profile fetches
  // (from a prior token or StrictMode synthetic remount) can be discarded.
  const profileFetchGenRef = useRef(0);

  // Refs to track current state for waitForProfile
  const profileRef = useRef(profile);
  const statusRef = useRef(status);
  const errorRef = useRef(error);

  useEffect(() => {
    profileRef.current = profile;
    statusRef.current = status;
    errorRef.current = error;
  }, [profile, status, error]);

  // Token change: only reset warmup state when the user actually switched.
  // A rotated token for the SAME user (the normal refresh path) must not
  // re-show the splash to returning users on every refresh — see #2568.
  useEffect(() => {
    const currentUserId = getSubFromToken(token);
    const userChanged = currentUserId !== prevWarmupUserIdRef.current;
    prevWarmupUserIdRef.current = currentUserId;
    if (!userChanged) return;
    warmupAttemptedRef.current = false;
    // For a different signed-in user, restore their persisted warmup flag so
    // returning users on the same browser don't re-splash; for a logout
    // (null user), drop back to false.
    setWarmupComplete(readWarmupFlag(currentUserId));
  }, [token]);

  // Paid status flip (Stripe upgrade `already_paid` race) genuinely
  // re-provisions the container, so reset warmup and clear the persisted
  // flag. Skip the very first run since prevIsPaidRef starts undefined.
  useEffect(() => {
    const newIsPaid = profile?.isPaidAccount;
    if (prevIsPaidRef.current === undefined) {
      prevIsPaidRef.current = newIsPaid;
      return;
    }
    if (newIsPaid === prevIsPaidRef.current) return;
    prevIsPaidRef.current = newIsPaid;
    warmupAttemptedRef.current = false;
    setWarmupComplete(false);
    clearWarmupFlag(getSubFromToken(token));
  }, [profile?.isPaidAccount, token]);

  const warmWorkspace = useCallback(async () => {
    if (!token) return;
    let warmupOk = false;
    try {
      const response = await fetchWithTimeout(
        `${AUTH_BASE_URL}/warmup`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        15000,
      );
      if (!response.ok) {
        console.warn(
          "[SessionProvider] Warmup request failed:",
          response.status,
        );
      } else {
        warmupOk = true;
      }
    } catch (err) {
      console.warn("[SessionProvider] Warmup request failed:", err);
    } finally {
      setWarmupComplete(true);
      // Persist for future refreshes — see #2568. Only persist on a
      // confirmed-success response; on transient failure the next refresh
      // should retry naturally rather than skip the splash incorrectly.
      if (warmupOk) {
        writeWarmupFlag(getSubFromToken(token));
      }
    }
  }, [token]);

  // Fire warmup after auth settles — don't fire during bootstrap to avoid 401 errors
  // with stale/expired tokens. The bootstrap effect refreshes expired tokens in parallel;
  // once it completes (status !== 'checking'), we know the token is valid.
  // The `isPaidAccount` dep ensures we re-trigger after the reset above runs on upgrade.
  useEffect(() => {
    if (!token) return;
    if (status === "checking") return;
    // A suspended account is a terminal state: don't bother warming a workspace
    // it can't reach (the warmup would just 403).
    if (status === "account_suspended") return;
    if (warmupAttemptedRef.current) return;
    warmupAttemptedRef.current = true;
    void warmWorkspace();
  }, [token, warmWorkspace, profile?.isPaidAccount, status]);

  // Generate or load guest ID
  const [guestId] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(GUEST_ID_STORAGE_KEY);
      if (stored) return stored;

      const newGuestId = `guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(GUEST_ID_STORAGE_KEY, newGuestId);
      return newGuestId;
    }
    return `guest-${Date.now()}`;
  });

  const queryClient = useQueryClient();

  // Fetch user profile and seat preview with retry logic.
  // Accepts an isStale check instead of an AbortSignal so stale fetches are
  // discarded without ever aborting the underlying request — aborting on
  // StrictMode's synthetic cleanup leaves `waitForProfile` polling forever.
  const fetchProfile = useCallback(
    async (isStale?: () => boolean) => {
      if (!token) {
        setProfile(null);
        setSeatPreview(null);
        return;
      }

      if (isStale?.()) return;

      setError(null);

      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [500, 1000, 2000]; // exponential backoff

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (isStale?.()) return;

        try {
          // Get stored email to help with routing in Docker/production
          const storedEmail =
            typeof window !== "undefined" ? getAuthItem("userEmail") : null;
          const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          };
          if (storedEmail) {
            headers["X-User-Email"] = storedEmail;
          }

          const response = await fetchWithTimeout(
            `${AUTH_BASE_URL}/auth/session`,
            {
              headers,
              cache: "no-store",
            },
            10000, // 10s timeout per attempt
          );

          if (!response.ok) {
            // Suspended account: the API 403s the boot session fetch
            // (Road 6). Surface a dedicated terminal state instead of the
            // generic connection-error / login-redirect path. The /auth/session
            // route only ever 403s for a suspended user, and the various auth
            // middlewares spell the body differently ('ACCOUNT_SUSPENDED',
            // 'Account suspended', 'Account is suspended or deleted'), so match
            // a 403 with any 'suspended' marker rather than one exact string.
            if (response.status === 403) {
              const body = await response.json().catch(() => null);
              const marker = `${body?.error ?? ""} ${body?.reason ?? ""} ${
                body?.message ?? ""
              }`.toLowerCase();
              if (
                marker.includes("account_suspended") ||
                marker.includes("suspended")
              ) {
                if (isStale?.()) return;
                console.warn(
                  "[SessionProvider] Account suspended — entering account_suspended state",
                );
                // Keep the token: this is a terminal account state, not an
                // auth failure. Do NOT clear it or attempt a refresh.
                setSessionError({
                  message:
                    "Your account has been suspended. Please contact support.",
                  operation: "accountSuspended",
                  code: "account_suspended",
                  timestamp: Date.now(),
                });
                setStatus("account_suspended");
                return;
              }
            }
            console.error(
              "[SessionProvider] Session invalid, status:",
              response.status,
            );
            const error: any = new Error("Session invalid");
            error.status = response.status;
            throw error;
          }

          const data = await response.json();

          const userProfile: UserProfile = {
            id: data.userId,
            name: data.userName || "",
            containerName: data.containerName || null,
            email: data.email,
            aiName: "Assistant",
            role: "assistant",
            onboardingCompleted: data.onboardingCompleted || false,
            onboardingComplete: data.onboardingComplete ?? data.onboardingCompleted ?? false,
            isPaidAccount: data.isPaidAccount || false,
            planPrice: data.planPrice || null,
            userNumber: data.userNumber || null,
            planLockedAt: data.planLockedAt || null,
            readOnly: data.readOnly || false,
            trialStartedAt: data.trialStartedAt ?? null,
            trialEndsAt: data.trialEndsAt ?? null,
            trialGraceEndsAt: data.trialGraceEndsAt ?? null,
            trialUsed: data.trialStatus?.trialUsed ?? false,
            trialStatus: data.trialStatus ?? null,
            agreementsAcceptedAt: data.agreementsAcceptedAt ?? null,
            requiresConsent: data.requiresConsent ?? null,
            timezone: data.timezone || null,
            loginProvider: data.loginProvider ?? null,
          };

          // Let a fresh-TRUE JWT claim lift onboardingCompleted over a
          // possibly-stale /auth/session cached read — ONE-DIRECTIONALLY.
          // The claim is minted at token issue and is NOT re-minted when
          // POST /onboarding/session/complete flips the DB latch, so right
          // after completing onboarding the STORED token carries a stale FALSE
          // for up to the token TTL (~15 min). The previous symmetric override
          // let that stale-false claim beat the server's fresh true, which
          // re-activated the onboarding gate on EVERY reload: sidebar
          // re-hidden, composer re-locked ~4s, beats re-seeded into the
          // last-open conversation, redundant session/complete re-POST
          // (reproduced 3/3 — #5228). A TRUE claim can never be stale (the
          // latch is one-way), so lifting only toward true keeps the original
          // fix for a stale-false /auth/session read while never dragging a
          // completed user back into onboarding. Keep both field spellings in
          // lockstep for the gate.
          const jwtPayload = decodeJwtPayload(token);
          if (jwtPayload && jwtPayload.onboardingCompleted === true) {
            userProfile.onboardingCompleted = true;
            userProfile.onboardingComplete = true;
          }

          if (isStale?.()) return;

          setProfile(userProfile);

          // Clear user-specific localStorage if onboarding not completed
          // This handles server-side resets (e.g., account deleted and recreated)
          if (!userProfile.onboardingCompleted) {
            clearUserSpecificStorage();
          }

          // Store userId and email in storage for API calls
          if (typeof window !== "undefined") {
            if (data.userId) {
              setAuthItem("userId", data.userId);
            }
            if (data.email) {
              setAuthItem("userEmail", data.email);
            }
          }

          // Calculate seat preview if user is not paid
          if (!userProfile.isPaidAccount) {
            const FOUNDER_LIMIT = 100;
            const nextSeatNumber = (userProfile.userNumber || 0) + 1;
            const planTier = "pro";
            const planPrice = nextSeatNumber <= FOUNDER_LIMIT ? 25 : 50;
            const spotsRemaining = Math.max(
              FOUNDER_LIMIT - nextSeatNumber + 1,
              0,
            );

            setSeatPreview({
              seatNumber: nextSeatNumber,
              planTier,
              planPrice,
              spotsRemaining,
            });
          } else {
            setSeatPreview(null);
          }

          // Success - break out of retry loop
          break;
        } catch (err) {
          console.error("[SessionProvider] Failed to fetch profile:", err);

          const e = err as any;
          const status = e?.status;

          // Handle timeout errors - create new error instead of modifying read-only property
          let errorToSet: Error = e;
          if (e?.name === "AbortError") {
            errorToSet = new Error("Session check timed out");
          }

          // On last attempt or 401, set error and handle accordingly
          if (attempt === MAX_RETRIES - 1 || status === 401) {
            // On 401, try refreshing the token before logging out
            // This handles expired access tokens on native apps
            if (status === 401) {
              const refreshResult = await refreshAccessTokenWithStatus(token);
              if (refreshResult.token) {
                if (isStale?.()) return;
                // Store new token and retry
                setTokenState(refreshResult.token);
                if (typeof window !== "undefined") {
                  setAuthItem(TOKEN_STORAGE_KEY, refreshResult.token);
                  if (refreshResult.email) {
                    setAuthItem("userEmail", refreshResult.email);
                  }
                }
                // Don't set error, let the useEffect re-trigger fetchProfile with new token
                return;
              }
              if (isStale?.()) return;
              // Refresh failed - now we can log out
              console.warn(
                "[SessionProvider] Token refresh failed, clearing session",
              );
              if (typeof window !== "undefined") {
                removeAuthItem(TOKEN_STORAGE_KEY);
                removeAuthItem("xavior_refresh_token");
                removeAuthItem("sessionToken");
                removeAuthItem("userId");
                removeAuthItem("userEmail");
              }
              setTokenState(null);
              setProfile(null);
              setSeatPreview(null);
              queryClient.clear();
            }
            if (isStale?.()) return;
            setError(errorToSet);
            // For non-401 errors, keep token and any existing profile
            // so we don't log user out for a network blip
          } else {
            // Not last attempt and not 401 - wait and retry
            await new Promise((resolve) =>
              setTimeout(resolve, RETRY_DELAYS[attempt]),
            );
            continue;
          }
        }
      }
    },
    [token, queryClient],
  );

  // Bootstrap effect: restore session from cookie or sessionStorage
  // This runs ONCE on mount and handles ALL initial auth logic
  useEffect(() => {
    const runId = ++bootstrapRunId.current;
    let cancelled = false;

    (async () => {
      setError(null);

      // If we already have a token in storage, use it
      const existing =
        typeof window !== "undefined" ? getAuthItem(TOKEN_STORAGE_KEY) : null;
      if (existing) {
        if (cancelled || bootstrapRunId.current !== runId) return;
        setTokenState(existing);
        setStatus("authenticated");
        setError(null);
        // fetchProfile will be triggered by the token state change
        return;
      }

      // No token in storage - try refresh cookie (retry on 503 — backend may be warming up)
      let refreshResult: {
        token: string | null;
        email?: string | null;
        status?: number;
        error?: Error;
      };
      const MAX_WARMUP_RETRIES = 5;
      const WARMUP_RETRY_DELAY = 3000;
      for (let attempt = 0; attempt <= MAX_WARMUP_RETRIES; attempt++) {
        refreshResult = await refreshAccessTokenWithStatus(existing);
        if (cancelled || bootstrapRunId.current !== runId) return;
        // Retry on 502/503 (backend warming up) — but not on auth failures
        if (
          refreshResult.status &&
          refreshResult.status >= 502 &&
          refreshResult.status <= 503 &&
          attempt < MAX_WARMUP_RETRIES
        ) {
          await new Promise((resolve) =>
            setTimeout(resolve, WARMUP_RETRY_DELAY),
          );
          if (cancelled || bootstrapRunId.current !== runId) return;
          continue;
        }
        break;
      }
      const {
        token: newToken,
        email: refreshedEmail,
        status: refreshStatus,
        error: refreshError,
      } = refreshResult!;

      if (cancelled || bootstrapRunId.current !== runId) return;

      if (newToken) {
        setTokenState(newToken);
        setStatus("authenticated");
        setError(null);
        // Store email for routing
        if (typeof window !== "undefined") {
          setAuthItem(TOKEN_STORAGE_KEY, newToken);
          if (refreshedEmail) {
            setAuthItem("userEmail", refreshedEmail);
          }
        }
      } else {
        // Dev auto-login: if VITE_DEV_AUTH_EMAIL is set, automatically login
        if (env.isDevelopment && env.devAuthEmail) {
          try {
            const devLoginResponse = await fetch(
              `${AUTH_BASE_URL}/auth/dev-login`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: env.devAuthEmail,
                  plan: "pro",
                  warm: true,
                }),
              },
            );
            if (devLoginResponse.ok) {
              const devLoginData = await devLoginResponse.json();
              // Prefer the JWT access token over the legacy session token (#1799):
              // matches the prod magic-link flow and avoids 401/402s where endpoints
              // reject a legacy hex token (e.g. POST /payment/confirm).
              const storedToken =
                (devLoginData.accessToken as string | undefined) ??
                (devLoginData.session as string | undefined);
              if (
                storedToken &&
                !cancelled &&
                bootstrapRunId.current === runId
              ) {
                setTokenState(storedToken);
                setStatus("authenticated");
                setError(null);
                if (typeof window !== "undefined") {
                  setAuthItem(TOKEN_STORAGE_KEY, storedToken);
                  setAuthItem("userEmail", env.devAuthEmail);
                }
                return; // Exit early - we're now authenticated
              }
            } else {
              console.warn(
                "[SessionProvider] Dev auto-login failed:",
                devLoginResponse.status,
              );
            }
          } catch (devLoginError) {
            console.warn(
              "[SessionProvider] Dev auto-login error:",
              devLoginError,
            );
          }
        }

        setTokenState(null);
        setStatus("unauthenticated");
        if (
          refreshError ||
          (typeof refreshStatus === "number" && refreshStatus >= 500)
        ) {
          const message =
            typeof refreshStatus === "number"
              ? `HTTP ${refreshStatus}: Auth service unavailable`
              : "Auth service unavailable";
          setError(refreshError || new Error(message));
        } else {
          setError(null);
        }
      }
    })().catch((err) => {
      console.error("[SessionProvider] Bootstrap error:", err);
      // Don't flip to unauthenticated on aborted/stale runs
      if (cancelled || bootstrapRunId.current !== runId) return;
      setTokenState(null);
      setError(err as Error);
      setStatus("unauthenticated");
    });

    return () => {
      cancelled = true;
    };
  }, []); // Empty deps - run ONCE on mount

  // Fetch profile when token changes. Uses a generation counter (not Abort-
  // Controller, whose StrictMode cleanup would abort the only in-flight fetch);
  // stale fetches from a previous token discard via isStale().
  useEffect(() => {
    const generation = ++profileFetchGenRef.current;

    if (!token) return;

    const isStale = () => profileFetchGenRef.current !== generation;

    const doFetch = async () => {
      try {
        await fetchProfile(isStale);
      } catch (err) {
        if (isStale()) return;
        console.error("[SessionProvider] Error in doFetch:", err);
        if (statusRef.current === "checking") {
          setStatus("unauthenticated");
        }
      }
    };

    void doFetch();
  }, [token, fetchProfile]);

  // Update token in state and storage
  // Native: localStorage (persists), Web: sessionStorage (clears on tab close)
  const setToken = useCallback((newToken: string | null) => {
    // Clear stale refs before the new token triggers a fresh fetch, so a
    // synchronously-awaited waitForProfile doesn't resolve with the previous
    // user's profile/error on re-login or account switch (same-user refreshes
    // accept a brief loader flash).
    errorRef.current = null;
    profileRef.current = null;
    setError(null);
    setProfile(null);
    setSeatPreview(null);

    setTokenState(newToken);

    // Update status immediately when token changes
    if (newToken) {
      setStatus("authenticated");
    } else {
      setStatus("unauthenticated");
    }

    if (typeof window !== "undefined") {
      if (newToken) {
        setAuthItem(TOKEN_STORAGE_KEY, newToken);
      } else {
        removeAuthItem(TOKEN_STORAGE_KEY);
      }
    }
  }, []);

  // Clear session
  const clearSession = useCallback(() => {
    setToken(null);
    setProfile(null);
    setSeatPreview(null);
    setStatus("unauthenticated");
    queryClient.clear();
    clearUserSpecificStorage();
    // Logout must STICK across an app relaunch (sprint native-oauth-pass Ch.2,
    // AC 5). clearAuthStorage() clears the FULL auth-storage key set (access +
    // refresh-in-localStorage + session + identity) — not just the access token
    // setToken(null) removes — sweeps the global paywall-dismissed + SSO-autoconnect
    // flags (replacing the prior explicit PAYWALL sweep here), AND purges the native
    // iOS Keychain refresh token, so the next bootstrap's header refresh can't
    // silently re-mint. Every logout path routes through it (clearSession, PlanGate).
    clearAuthStorage();
  }, [setToken, queryClient]);

  // #5420: lib/http.ts dispatches this app-wide event when an unrecoverable
  // 401 (token-refresh failure) clears the stored token mid-session. Without
  // this listener the token is gone but `status` never flips, so the running
  // SPA keeps firing requests that 401 and renders misleading feature-level
  // errors ("Couldn't open that module") instead of routing to /login.
  // Mirrors the xavior:workspace-locked precedent (BillingIssueModalHost).
  // The event name is duplicated as a literal in lib/http.ts (the dispatch
  // site) because lib must not import from features — keep the two in sync.
  useEffect(() => {
    const handler = () => clearSession();
    window.addEventListener("xavior:session-expired", handler);
    return () => window.removeEventListener("xavior:session-expired", handler);
  }, [clearSession]);

  // Refresh profile (for "I already upgraded" button)
  const refresh = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  // Proactive token refresh off the JWT `exp` claim (#2570): schedule a refresh
  // 60s before expiry (fire now if already inside that window); on success swap
  // the token and reschedule; on failure clear the token + raise a sessionError
  // so the UI prompts re-login instead of silently 401-ing. (A coarse
  // setInterval is unreliable — throttled background tabs / skipped ticks.)
  useEffect(() => {
    if (!token) return;

    const exp = readJwtExpSeconds(token);
    if (exp === null) {
      // Non-JWT (e.g. legacy hex session token) — fall back to the old 14-min
      // cadence so we don't lose the periodic refresh entirely.
      const refreshInterval = setInterval(
        () => {
          void (async () => {
            const newToken = await refreshAccessToken();
            if (newToken) {
              setTokenState(newToken);
              if (typeof window !== "undefined") {
                setAuthItem(TOKEN_STORAGE_KEY, newToken);
              }
            }
          })();
        },
        14 * 60 * 1000,
      );
      return () => clearInterval(refreshInterval);
    }

    const nowMs = Date.now();
    const refreshAtMs = exp * 1000 - 60_000; // 60s before exp
    const delayMs = Math.max(0, refreshAtMs - nowMs);

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const result = await refreshAccessTokenWithStatus(token);
      if (cancelled) return;

      if (result.token) {
        setTokenState(result.token);
        if (typeof window !== "undefined") {
          setAuthItem(TOKEN_STORAGE_KEY, result.token);
        }
        return;
      }

      // Refresh failed — token is now expired or about to be. Clear state and
      // surface the error so the UI can prompt the user to sign in again.
      console.warn("[SessionProvider] Token refresh failed, clearing session", {
        status: result.status,
        error: result.error?.message,
      });
      setTokenState(null);
      if (typeof window !== "undefined") {
        removeAuthItem(TOKEN_STORAGE_KEY);
      }
      setSessionError({
        message: "Your session expired. Please sign in again.",
        operation: "tokenRefresh",
        timestamp: Date.now(),
      });
      setStatus("unauthenticated");
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token]);

  // Wait for profile to load after setting token.
  // Resolves on terminal error as well as success so callers (MagicLinkVerify)
  // don't sit on "Verifying..." for the full 40s when the fetch has already failed.
  const waitForProfile = useCallback(async (): Promise<UserProfile | null> => {
    const maxWaitTime = 40000; // 40 seconds
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkProfile = () => {
        const currentProfile = profileRef.current;
        const currentError = errorRef.current;

        if (currentProfile) {
          resolve(currentProfile);
          return;
        }

        if (currentError) {
          resolve(null);
          return;
        }

        if (Date.now() - startTime > maxWaitTime) {
          resolve(null);
          return;
        }

        setTimeout(checkProfile, 100);
      };

      checkProfile();
    });
  }, []);

  // Update user profile (name, AI name, onboarding status)
  const updateProfile = useCallback(
    async (updates: ProfileUpdate): Promise<void> => {
      if (!token) {
        console.error("[SessionProvider] Cannot update profile without token");
        throw new Error("No authentication token");
      }

      const response = await fetchWithTimeout(
        `${AUTH_BASE_URL}/auth/profile`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        },
        10000,
      );

      if (!response.ok) {
        console.error(
          "[SessionProvider] Failed to update profile:",
          response.status,
        );
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      // Update local profile state
      setProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(updates.name !== undefined && { name: updates.name }),
          ...(updates.aiName !== undefined && { aiName: updates.aiName }),
          // Onboarding completion patches BOTH fields in lockstep: the legacy
          // `onboardingCompleted` (kept for old callers) and the NEW
          // `onboardingComplete` the app gate reads. They must never diverge,
          // or completing onboarding here won't lift the gate.
          ...(updates.onboardingCompleted !== undefined && {
            onboardingCompleted: updates.onboardingCompleted,
            onboardingComplete: updates.onboardingCompleted,
          }),
        };
      });

      // Invalidate team members cache so other users see updated name
      queryClient.invalidateQueries({ queryKey: ["workspace-team-members"] });
    },
    [token, queryClient],
  );

  // Fetch containers and pending invites
  const fetchContainers = useCallback(async () => {
    if (!token) {
      setContainers([]);
      setPendingInvites([]);
      setContainersLoaded(false);
      return;
    }

    try {
      const response = await fetchWithTimeout(
        `${AUTH_BASE_URL}/auth/containers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        10000,
      );

      if (!response.ok) {
        console.error(
          "[SessionProvider] Failed to fetch containers:",
          response.status,
        );
        if ([404, 405, 501, 502, 503].includes(response.status)) {
          // Non-fatal in single-workspace/local setups where containers aren't exposed.
          setContainers([]);
          setPendingInvites([]);
          setSessionError(null);
          setContainersLoaded(true);
          return;
        }
        setSessionError({
          message: "Failed to load workspaces. Please try again.",
          operation: "fetchContainers",
          timestamp: Date.now(),
        });
        return;
      }

      const data = await response.json();

      // Map snake_case API response to camelCase TypeScript types
      const mappedContainers: ContainerAccess[] = (data.containers || []).map(
        mapContainerFromList,
      );

      const mappedInvites: PendingInvite[] = (data.pendingInvites || []).map(
        (inv: any) => ({
          id: inv.id,
          ownerId: inv.owner_id,
          ownerEmail: inv.owner_email,
          ownerName: inv.owner_name || null,
          containerName: inv.container_name || null,
          permission: inv.permission,
          invitedAt: inv.invited_at,
          ignoredAt: inv.ignored_at || null,
        }),
      );

      setContainers(mappedContainers);
      setPendingInvites(mappedInvites);
      setSessionError(null);
      setContainersLoaded(true);

      // #5105: the gate chain runs on THIS response (+ cached selection), not the
      // raw DB row — so capture exactly what the FE received. For a member-only of
      // an active team this is where a runtime-vs-DB mismatch would show.
      diag("containers", `GET /auth/containers → ${mappedContainers.length} container(s), ${mappedInvites.length} invite(s)`, {
        containers: mappedContainers.map(summarizeContainer),
        pendingInvites: mappedInvites.length,
      });

      const selectedContainerId = selectedContainer?.id;
      const selectedOwnerId = selectedContainer?.ownerId;
      const matchingSelectedContainer = selectedContainerId
        ? mappedContainers.find(
            (c: ContainerAccess) => c.id === selectedContainerId,
          )
        : selectedOwnerId
          ? mappedContainers.find(
              (c: ContainerAccess) => c.ownerId === selectedOwnerId,
            )
          : undefined;
      const hasExistingSelection = Boolean(matchingSelectedContainer);

      if (!selectedContainer || !hasExistingSelection) {
        if (mappedContainers.length === 0) {
          setSelectedContainer(null);
          if (typeof window !== "undefined") {
            removeAuthItem(SELECTED_CONTAINER_KEY);
          }
        } else {
          const firstOwned = mappedContainers.find(
            (c: ContainerAccess) => c.isOwner,
          );
          const defaultContainer = firstOwned || mappedContainers[0];
          setSelectedContainer(defaultContainer);
          if (typeof window !== "undefined") {
            setAuthItem(
              SELECTED_CONTAINER_KEY,
              JSON.stringify(defaultContainer),
            );
          }
        }
      } else if (matchingSelectedContainer) {
        const selectionChanged =
          selectedContainer.id !== matchingSelectedContainer.id ||
          selectedContainer.ownerId !== matchingSelectedContainer.ownerId ||
          selectedContainer.ownerEmail !==
            matchingSelectedContainer.ownerEmail ||
          selectedContainer.ownerName !== matchingSelectedContainer.ownerName ||
          selectedContainer.containerName !==
            matchingSelectedContainer.containerName ||
          selectedContainer.permission !==
            matchingSelectedContainer.permission ||
          selectedContainer.isOwner !== matchingSelectedContainer.isOwner ||
          selectedContainer.memberSince !==
            matchingSelectedContainer.memberSince ||
          // Teams Stream 1 (#3621): re-persist when the canonical workspace
          // classification changes so the cached selection stays current.
          selectedContainer.type !== matchingSelectedContainer.type ||
          selectedContainer.planTier !== matchingSelectedContainer.planTier ||
          selectedContainer.role !== matchingSelectedContainer.role ||
          // Per-workspace onboarding state: re-persist when either field changes
          // so downstream gates always see the current API value.
          selectedContainer.onboardingState !== matchingSelectedContainer.onboardingState ||
          selectedContainer.onboardingCompleted !== matchingSelectedContainer.onboardingCompleted;

        if (selectionChanged) {
          setSelectedContainer(matchingSelectedContainer);
          if (typeof window !== "undefined") {
            setAuthItem(
              SELECTED_CONTAINER_KEY,
              JSON.stringify(matchingSelectedContainer),
            );
          }
        }
      }
    } catch (err) {
      console.error("[SessionProvider] Error fetching containers:", err);
      if (env.isDevelopment) {
        // Avoid noisy banners when auth gateway isn't available in local dev.
        setContainers([]);
        setPendingInvites([]);
        setSessionError(null);
        setContainersLoaded(true);
        return;
      }
      setSessionError({
        message: "Failed to load workspaces. Please check your connection.",
        operation: "fetchContainers",
        timestamp: Date.now(),
      });
    }
  }, [token, selectedContainer]);

  // Select a container and get new token with container context.
  //
  // `reload` (default true) does a hard window.location.reload() after the token
  // swap so all module data refetches from the new container — correct for an
  // EXPLICIT user workspace-switch. The automatic container-sync effect below
  // passes `reload: false`: a hard reload there wipes the in-memory
  // `containerSyncAttemptRef` guard, and when a member's JWT active-workspace
  // never converges to the team (it resolves to the legacy user workspace,
  // server-confirmed #5105), that turns the sync into an infinite
  // select→reload→select loop. Without the reload, the guard holds and the sync
  // attempts at most once; the token swap + queryClient.clear() already refetch
  // workspace data via React (no full reload needed).
  const selectContainer = useCallback(
    async (
      containerId: string,
      opts?: { reload?: boolean },
    ): Promise<{ success: boolean; status?: number }> => {
      if (!token) return { success: false };
      const reload = opts?.reload !== false;
      diag("auth", `selectContainer ${containerId}`, { reload });

      try {
        const response = await fetchWithTimeout(
          `${AUTH_BASE_URL}/auth/select-container`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ containerId }),
          },
          10000,
        );

        if (!response.ok) {
          console.error(
            "[SessionProvider] Failed to select container:",
            response.status,
          );
          return { success: false, status: response.status };
        }

        const data = await response.json();

        // Update token with container context
        if (data.accessToken) {
          setTokenState(data.accessToken);
          if (typeof window !== "undefined") {
            setAuthItem(TOKEN_STORAGE_KEY, data.accessToken);
          }
        }

        // Update selected container
        const newContainer: ContainerAccess =
          mapContainerFromSelect(data.container);
        setSelectedContainer(newContainer);
        if (typeof window !== "undefined") {
          setAuthItem(SELECTED_CONTAINER_KEY, JSON.stringify(newContainer));
        }

        // Clear all cached data so queries refetch against the new container.
        queryClient.clear();
        // Email caches live in sessionStorage and survive the reload below —
        // purge them so the previous container's mail never renders (#5276).
        clearEmailCaches();

        // Hard-reload only for an explicit user switch. The automatic sync path
        // passes reload:false to avoid the #5105 member reload loop (see the
        // selectContainer doc comment); the token swap above + this cache clear
        // re-scope query data via React without a full reload. A mounted email
        // module surviving this no-reload path drops its in-memory rows via the
        // cache-epoch bump inside clearEmailCaches (#5295).
        if (reload) {
          window.location.reload();
        }
        return { success: true };
      } catch (err) {
        console.error("[SessionProvider] Error selecting container:", err);
        return { success: false };
      }
    },
    [token, queryClient],
  );

  // Accept a pending invite
  const acceptInvite = useCallback(
    async (inviteId: string) => {
      if (!token) return;

      try {
        const response = await fetchWithTimeout(
          `${env.apiBaseUrl}/workspaces/invites/${inviteId}/accept`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          10000,
        );

        if (!response.ok) {
          console.error(
            "[SessionProvider] Failed to accept invite:",
            response.status,
          );
          return;
        }

        // Refresh containers list
        await fetchContainers();
      } catch (err) {
        console.error("[SessionProvider] Error accepting invite:", err);
      }
    },
    [token, fetchContainers],
  );

  // Ignore a pending invite (soft ignore - can still accept later)
  const ignoreInvite = useCallback(
    async (inviteId: string) => {
      if (!token) return;

      try {
        const response = await fetchWithTimeout(
          `${env.apiBaseUrl}/workspaces/invites/${inviteId}/ignore`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          10000,
        );

        if (!response.ok) {
          console.error(
            "[SessionProvider] Failed to ignore invite:",
            response.status,
          );
          setSessionError({
            message: "Failed to ignore invite. Please try again.",
            operation: "ignoreInvite",
            timestamp: Date.now(),
          });
          return;
        }

        // Refresh containers list (will update ignoredAt on the invite)
        await fetchContainers();
      } catch (err) {
        console.error("[SessionProvider] Error ignoring invite:", err);
        setSessionError({
          message: "Failed to ignore invite. Please check your connection.",
          operation: "ignoreInvite",
          timestamp: Date.now(),
        });
      }
    },
    [token, fetchContainers],
  );

  // Reject a pending invite
  const rejectInvite = useCallback(
    async (inviteId: string) => {
      if (!token) return;

      try {
        const response = await fetchWithTimeout(
          `${env.apiBaseUrl}/workspaces/invites/${inviteId}/reject`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          10000,
        );

        if (!response.ok) {
          console.error(
            "[SessionProvider] Failed to reject invite:",
            response.status,
          );
          return;
        }

        // Remove from pending invites
        setPendingInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      } catch (err) {
        console.error("[SessionProvider] Error rejecting invite:", err);
      }
    },
    [token],
  );

  // Leave a workspace (changes accepted status to ignored)
  const leaveWorkspace = useCallback(
    async (ownerId: string) => {
      if (!token) return;
      const wsId = containers.find((c) => c.ownerId === ownerId && !c.isOwner)?.id;
      if (!wsId) return;
      try {
        const response = await fetchWithTimeout(
          `${env.apiBaseUrl}/workspaces/${wsId}/leave`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          10000,
        );

        if (!response.ok) {
          console.error(
            "[SessionProvider] Failed to leave workspace:",
            response.status,
          );
          setSessionError({
            message: "Failed to leave workspace. Please try again.",
            operation: "leaveWorkspace",
            timestamp: Date.now(),
          });
          return;
        }

        // If we were in this workspace, switch to our own
        if (selectedContainer?.ownerId === ownerId) {
          const ownContainer = containers.find((c) => c.isOwner);
          if (ownContainer) {
            await selectContainer(ownContainer.id);
          }
        }

        await fetchContainers();
      } catch (err) {
        console.error("[SessionProvider] Error leaving workspace:", err);
        setSessionError({
          message: "Failed to leave workspace. Please check your connection.",
          operation: "leaveWorkspace",
          timestamp: Date.now(),
        });
      }
    },
    [token, selectedContainer, containers, selectContainer, fetchContainers],
  );

  // Reset containersLoaded on token change so a stale "loaded" from the previous
  // identity can't green-light action on transiently-empty containers mid-fetch.
  useEffect(() => { setContainersLoaded(false); }, [token]);

  useEffect(() => {
    if (token && profile) {
      void fetchContainers();
    }
  }, [token, profile, fetchContainers]);

  // Ensure stored container selection matches the active JWT workspace context
  useEffect(() => {
    if (!token || !selectedContainer) return;

    // Use the unique space ID, falling back to ownerId for cached containers
    // that predate the backend returning the id field
    const desiredWorkspaceId =
      selectedContainer.id || selectedContainer.ownerId;
    if (!desiredWorkspaceId) return;

    const activeWorkspaceId = getActiveWorkspaceIdFromToken(token);

    // Check if the JWT's active workspace matches the selected container's ID
    // (JWT claim is still `activeTenantId` — frozen supervisor protocol)
    // For own workspace without explicit active workspace in token, also accept userId match
    const selectionMatches =
      activeWorkspaceId === desiredWorkspaceId ||
      (selectedContainer.isOwner && !activeWorkspaceId);

    if (selectionMatches) {
      containerSyncAttemptRef.current = null;
      return;
    }

    if (containerSyncAttemptRef.current === desiredWorkspaceId) {
      return;
    }

    containerSyncAttemptRef.current = desiredWorkspaceId;
    (async () => {
      // reload:false — a hard reload here wipes containerSyncAttemptRef (it's an
      // in-memory ref), which for a member whose JWT active-workspace never
      // converges to the team turns this into an infinite select→reload loop
      // (#5105, server-confirmed: 259 select calls in 5 min, all 200). Without
      // the reload the guard holds → at most one attempt per workspace.
      const result = await selectContainer(desiredWorkspaceId, { reload: false });
      if (!result.success) {
        containerSyncAttemptRef.current = null;
        if (result.status === 403 || result.status === 404) {
          console.warn(
            "[SessionProvider] Lost access to selected workspace, clearing selection",
          );
          setSelectedContainer(null);
          if (typeof window !== "undefined") {
            removeAuthItem(SELECTED_CONTAINER_KEY);
          }
          await fetchContainers();
        }
      }
    })();
  }, [token, selectedContainer, selectContainer, fetchContainers]);

  // Read-only mode: true when user has read-only permission on current container or trial grace period
  const isReadOnly =
    selectedContainer?.permission === "read" || Boolean(profile?.readOnly);

  // Container warmup gate: ready when warmup is done OR user doesn't need a container
  const needsContainer = Boolean(
    profile?.isPaidAccount ||
    profile?.trialStatus?.isTrialActive ||
    profile?.trialStatus?.isGracePeriod,
  );
  const containerReady = !profile ? false : !needsContainer || warmupComplete;

  const value: SessionContextValue = {
    token,
    guestId,
    profile,
    seatPreview,
    status,
    isLoading, // Derived from status for backwards compatibility
    error,
    setToken,
    clearSession,
    refresh,
    waitForProfile,
    updateProfile,
    // Container/team support
    containers,
    pendingInvites,
    selectedContainer,
    selectContainer,
    fetchContainers,
    acceptInvite,
    ignoreInvite,
    rejectInvite,
    leaveWorkspace,
    isReadOnly,
    // Session errors for user feedback
    sessionError,
    clearSessionError,
    containerReady,
    containersLoaded,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

export type { SessionError };
